import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { BoxOfferInvitationStatus } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferInvitations } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxPurchases } from "@modules/boxPurchases/hooks/useBoxPurchases"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { word } from "@modules/core/utils/i18n"
import { MemberOffersList } from "@modules/members/memberOffers/components/memberOffersList"
import { HUText } from "@modules/ui/components/huText"
import { MenuItem } from "primereact/menuitem"
import { TabMenu } from "primereact/tabmenu"
import { useMemo, useState, useTransition } from "react"
import { useTheme } from "styled-components"

type Props = {
  memberId: string
  userId: string
  setSubscriptionSelected?: React.Dispatch<React.SetStateAction<BoxSubscriptionDetailsEntity | null>>
  setIsOfferSupensionVisible?: React.Dispatch<React.SetStateAction<boolean>>
  setIsOfferDiscountVisible?: React.Dispatch<React.SetStateAction<boolean>>
  handleDeleteSubscription?: () => void
  handleShowCreditModal?: (consumableDetails: BoxConsumableDetailsEntity) => void
  className?: string
} & React.ComponentProps<"div">

export function MemberOffersWrapper({
  memberId,
  userId,
  setSubscriptionSelected,
  setIsOfferSupensionVisible,
  setIsOfferDiscountVisible,
  handleDeleteSubscription,
  handleShowCreditModal,
  className,
  ...props
}: Props) {
  const defaultClassName = "flex flex-column w-full gap-3"

  const emptyText = {
    active: word("box.members.tab.offer.emptyText.noActive"),
    pending: word("box.members.tab.offer.emptyText.noPending"),
    expired: word("box.members.tab.offer.emptyText.noExpired"),
  }
  const theme = useTheme()
  const [, startTransition] = useTransition()
  const { data: dataPurchases } = useBoxPurchases(memberId)
  const { data: dataInvitations } = useBoxOfferInvitations(userId)
  const [activeMembershipsIndex, setActiveMembershipsIndex] = useState(0)

  const activesPurchases = useMemo(() => dataPurchases.getActives(), [dataPurchases])
  const expiredsPurchases = useMemo(() => dataPurchases.getExpireds(), [dataPurchases])
  const pendingInvitations = useMemo(
    () => dataInvitations.filter((invitation) => invitation.status === BoxOfferInvitationStatus.PENDING),
    [dataInvitations],
  )
  const expiredInvitations = useMemo(
    () =>
      dataInvitations.filter((invitation) =>
        [BoxOfferInvitationStatus.REFUSED, BoxOfferInvitationStatus.CANCELLED].includes(invitation.status),
      ),
    [dataInvitations],
  )

  const items: MenuItem[] = [
    {
      template: () => (
        <HUText
          fontStyle="LM"
          color={theme.neutralColor900}
          className={`flex cursor-pointer rounded-full px-3 py-2 mr-2 border-1 border-solid border-neutralColor-200
              ${activeMembershipsIndex === 0 ? "bg-neutralColor-200" : ""}`}
          onClick={() => {
            selectTab(0)
          }}
        >
          {word("box.members.tab.offer.menu.activeMemberships")} (
          {activesPurchases.activeSubscriptions.length + activesPurchases.activeConsumables.length})
        </HUText>
      ),
    },
    {
      template: () => (
        <HUText
          fontStyle="LM"
          color={theme.neutralColor900}
          className={`flex cursor-pointer rounded-full px-3 py-2 mr-2 border-1 border-solid border-neutralColor-200
              ${activeMembershipsIndex === 1 ? "bg-neutralColor-200" : ""}`}
          onClick={() => {
            selectTab(1)
          }}
        >
          {word("box.members.tab.offer.menu.pending")} ({pendingInvitations.length})
        </HUText>
      ),
    },
    {
      template: () => (
        <HUText
          fontStyle="LM"
          color={theme.neutralColor900}
          className={`flex cursor-pointer rounded-full px-3 py-2 mr-2 border-1 border-solid border-neutralColor-200
              ${activeMembershipsIndex === 2 ? "bg-neutralColor-200" : ""}`}
          onClick={() => {
            selectTab(2)
          }}
        >
          {word("box.members.tab.offer.menu.expiredAndDeclined")} (
          {expiredsPurchases.expiredConsumables.length +
            expiredsPurchases.expiredSubscriptions.length +
            expiredInvitations.length}
          )
        </HUText>
      ),
    },
  ]

  function selectTab(index: number) {
    startTransition(() => {
      setActiveMembershipsIndex(index)
    })
  }

  return (
    <div className={className ?? defaultClassName} {...props}>
      <div className="flex w-full">
        <TabMenu model={items} activeIndex={activeMembershipsIndex} className="w-full border-0 mb-0" />
      </div>
      <div className="flex w-full">
        {activeMembershipsIndex === 0 && (
          <MemberOffersList
            memberId={memberId}
            boxConsumablesDetails={activesPurchases.activeConsumables}
            boxSubscriptionsDetails={activesPurchases.activeSubscriptions}
            emptyText={emptyText.active}
            setSubscriptionSelected={setSubscriptionSelected}
            setIsOfferSupensionVisible={setIsOfferSupensionVisible}
            setIsOfferDiscountVisible={setIsOfferDiscountVisible}
            handleDeleteSubscription={handleDeleteSubscription}
            handleShowCreditModal={handleShowCreditModal}
          />
        )}
        {activeMembershipsIndex === 1 && (
          <MemberOffersList
            memberId={memberId}
            boxOfferInvitations={pendingInvitations}
            emptyText={emptyText.pending}
            setSubscriptionSelected={setSubscriptionSelected}
            setIsOfferSupensionVisible={setIsOfferSupensionVisible}
            setIsOfferDiscountVisible={setIsOfferDiscountVisible}
            handleDeleteSubscription={handleDeleteSubscription}
            handleShowCreditModal={handleShowCreditModal}
          />
        )}
        {activeMembershipsIndex === 2 && (
          <MemberOffersList
            memberId={memberId}
            boxOfferInvitations={expiredInvitations}
            boxConsumablesDetails={expiredsPurchases.expiredConsumables}
            boxSubscriptionsDetails={expiredsPurchases.expiredSubscriptions}
            emptyText={emptyText.expired}
            setSubscriptionSelected={setSubscriptionSelected}
            setIsOfferSupensionVisible={setIsOfferSupensionVisible}
            setIsOfferDiscountVisible={setIsOfferDiscountVisible}
            handleDeleteSubscription={handleDeleteSubscription}
            handleShowCreditModal={handleShowCreditModal}
          />
        )}
      </div>
    </div>
  )
}
